import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faDesktop,
  faMobileScreenButton,
  faRotateRight,
  faTabletScreenButton,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgSegmentPills from 'modules/Common/ui/CgSegmentPills';

// The studio canvas: a toolbar (address bar + device/zoom controls) over a
// scaled live preview of the real public surface.
//
// WHY the transform-scale dance: the public portal page is a responsive
// surface (its content column is max-w-[1200px] — see PublicCatalogSharePage),
// so rendering it inside a ~400px preview column only ever showed its MOBILE
// breakpoint. Design tools solve this by rendering at a real device width and
// scaling the result down, which is what happens below: the iframe keeps a
// desktop LOGICAL viewport (1280px) while occupying whatever space the canvas
// has. Suppliers therefore preview what their customers actually get.
const PREVIEW_DEVICES = [
  // 1280 clears the public page's 1200px content column plus its px-6 gutters,
  // i.e. the narrowest width that still renders the true desktop layout.
  { id: 'desktop', label: 'Desktop', icon: faDesktop, width: 1280 },
  { id: 'tablet', label: 'Tablet', icon: faTabletScreenButton, width: 834 },
  { id: 'mobile', label: 'Mobile', icon: faMobileScreenButton, width: 390 },
];

const ZOOM_LEVELS = [
  { id: 'fit', label: 'Fit' },
  { id: '0.75', label: '75%' },
  { id: '1', label: '100%' },
];

// Both toolbar strips (device, zoom) render as one control language.
const SEGMENT_STRIP_CLASS =
  'flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1';

// Fallbacks for the first paint (and jsdom, where the ResizeObserver stub
// never reports a box) so the frame is never rendered at scale 0.
const FALLBACK_STAGE = { width: 960, height: 620 };

// A dot grid reads as "canvas", separating the framed page from app chrome
// without adding another heavy border or shadow.
//
// The inset is INLINE, not a `p-*` class, because it is real layout
// arithmetic: every pixel here is taken off the preview's width, then
// multiplied by the fit scale.
const CANVAS_STYLE = {
  backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(203 213 225) 1px, transparent 0)',
  backgroundSize: '16px 16px',
  padding: 12,
};

export default function PortalPreviewCanvas({
  url,
  src,
  frameKey,
  title = 'Preview',
  dirty = false,
  onRefresh,
  emptyState = null,
}) {
  const [deviceId, setDeviceId] = useState('desktop');
  const [zoomId, setZoomId] = useState('fit');
  const stageRef = useRef(null);
  const [stage, setStage] = useState({ width: 0, height: 0 });

  // The scale depends on the space the canvas actually has, which changes with
  // the window, the sidebar and the wrap point — so it is measured, not assumed.
  useEffect(() => {
    const element = stageRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setStage({ width: Math.round(rect.width), height: Math.round(rect.height) });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Both, not just `src`: the toolbar's address bar and its open-in-new-tab
  // button are driven by `url`, so a preview without one would offer to open
  // `undefined`.
  const hasPreview = !!(src && url);
  const device = PREVIEW_DEVICES.find((entry) => entry.id === deviceId) || PREVIEW_DEVICES[0];
  const availableWidth = stage.width || FALLBACK_STAGE.width;
  const availableHeight = stage.height || FALLBACK_STAGE.height;
  const fitScale = Math.min(1, availableWidth / device.width);
  const scale = zoomId === 'fit' ? fitScale : Number(zoomId);
  // The iframe's logical viewport is `device.width` wide and exactly as tall as
  // the visible canvas once scaled — so the previewed page scrolls INSIDE its
  // frame like a real browser window, instead of the canvas scrolling one
  // giant scaled page (which would give two nested scrollbars).
  const viewportHeight = Math.max(360, Math.round(availableHeight / scale));

  return (
    // Deliberately no `h-[560px] xl:h-auto` pair (CLAUDE.md "two cascade
    // hazards" #1): the section is always auto/stretch — it fills the studio row
    // at `xl` and, when the panes stack, takes its height from the stage's
    // min-height below.
    <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
      {/* Toolbar — doubles as the browser chrome, so the frame below needs no
          second address bar of its own. */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50/80">
        {hasPreview ? (
          <CgSegmentPills
            segments={PREVIEW_DEVICES}
            activeSegmentId={deviceId}
            onSegmentChange={setDeviceId}
            className={SEGMENT_STRIP_CLASS}
          />
        ) : null}

        <div className="flex-1 min-w-[140px] flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" />
          <span className="text-[11px] font-mono text-slate-500 truncate">
            {url || 'Not published yet'}
          </span>
        </div>

        {hasPreview ? (
          <>
            <div className="flex items-center gap-2">
              <CgSegmentPills
                segments={ZOOM_LEVELS}
                activeSegmentId={zoomId}
                onSegmentChange={setZoomId}
                className={SEGMENT_STRIP_CLASS}
              />
              <span className="text-[11px] text-slate-400 tabular-nums w-9">
                {Math.round(scale * 100)}%
              </span>
            </div>
            <div className="flex items-center gap-1">
              <CgRadixButton
                variant="ghost"
                size="sm"
                icon={faRotateRight}
                onClick={onRefresh}
                title="Reload the preview"
                aria-label="Reload the preview"
              />
              <CgRadixButton
                variant="ghost"
                size="sm"
                icon={faArrowUpRightFromSquare}
                onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                title="Open in a new tab"
                aria-label="Open in a new tab"
              />
            </div>
          </>
        ) : null}
      </div>

      {/* The preview only ever shows PUBLISHED content (it loads the real
          public route), so unsaved edits must be called out rather than
          silently misrepresenting the page. */}
      {dirty && hasPreview ? (
        <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-700">
          Unsaved changes — the preview shows the published version until you save.
        </div>
      ) : null}

      {/* min-h keeps the stage measurable (and the frame sane) when the panes
          stack below `xl` and nothing else gives this column a height. */}
      <div
        ref={stageRef}
        className="flex-1 min-h-[420px] overflow-auto bg-slate-100"
        style={CANVAS_STYLE}
      >
        {hasPreview ? (
          <div
            className="mx-auto rounded-xl border border-slate-300/70 bg-white shadow-lg overflow-hidden"
            style={{
              width: Math.round(device.width * scale),
              height: Math.round(viewportHeight * scale),
            }}
          >
            <iframe
              key={frameKey}
              src={src}
              title={title}
              className="block border-0 bg-white"
              style={{
                width: device.width,
                height: viewportHeight,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            />
          </div>
        ) : (
          <div className="h-full min-h-[240px] flex items-center justify-center">{emptyState}</div>
        )}
      </div>
    </section>
  );
}
